/**
 * useFormRecommendations Hook
 *
 * Phase 7.2: Recommendation Engine
 *
 * React Query hook for fetching form recommendations based on job context.
 * Returns matching user forms, system templates, and suggestions.
 */

import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

// =============================================================================
// TYPES
// =============================================================================

export interface MatchingForm {
  id: string;
  form_name: string;
  description: string | null;
  jurisdiction_code: string | null;
  permit_type: string | null;
  fields: unknown[];
  times_used: number;
  is_favorite: boolean;
  created_at: string;
  matchScore: number;
  matchReason: string;
}

export interface FormSuggestion {
  id: string;
  templateId?: string;
  name: string;
  purpose: string;
  category?: string;
  pdfUrl?: string;
  officialSource?: string;
  fieldCount?: number;
  requiredFields?: string[];
  notes?: string;
  source: 'jurisdiction' | 'system';
  jurisdictionCode: string | null;
  permitType: string | null;
}

export interface OtherUserForm {
  id: string;
  form_name: string;
  description: string | null;
  jurisdiction_code: string | null;
  permit_type: string | null;
  times_used: number;
  is_favorite: boolean;
}

export interface FormRecommendationsResult {
  success: boolean;
  jurisdiction: {
    id: number;
    code: string;
    name: string;
    isHvhz: boolean;
  } | null;
  recommendations: {
    matchingForms: MatchingForm[];
    otherUserForms: OtherUserForm[];
    suggestions: FormSuggestion[];
  };
  stats: {
    totalUserForms: number;
    matchingCount: number;
    suggestionsCount: number;
  };
}

export interface FormRecommendationsParams {
  jurisdictionId?: number | null;
  permitType?: string | null;
  jobScope?: string | null;
  jobId?: string | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to fetch form recommendations based on job context
 *
 * @param params - Job context parameters
 * @param enabled - Whether to enable the query
 */
export function useFormRecommendations(
  params: FormRecommendationsParams,
  enabled: boolean = true
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['formRecommendations', params],
    queryFn: async () => {
      const getFormRecommendations = httpsCallable<
        FormRecommendationsParams,
        FormRecommendationsResult
      >(functions, 'getFormRecommendations');

      const result = await getFormRecommendations(params);
      return result.data;
    },
    enabled: enabled && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes cache
  });
}

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * Hook to check if user has any forms that can be auto-filled for a job
 */
export function useHasMatchingForms(params: FormRecommendationsParams) {
  const { data, isLoading } = useFormRecommendations(params);

  return {
    hasMatchingForms: (data?.recommendations.matchingForms.length ?? 0) > 0,
    matchingCount: data?.recommendations.matchingForms.length ?? 0,
    hasSuggestions: (data?.recommendations.suggestions.length ?? 0) > 0,
    suggestionsCount: data?.recommendations.suggestions.length ?? 0,
    isLoading,
  };
}

/**
 * Hook to get the best matching form for a job
 */
export function useBestMatchingForm(params: FormRecommendationsParams) {
  const { data, isLoading } = useFormRecommendations(params);

  const bestMatch = data?.recommendations.matchingForms[0] ?? null;

  return {
    bestMatch,
    isLoading,
  };
}
